import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Input, Select } from '@st-operations/uikit/src/v2'

import ValidatedForm from '_components/validated-form/index.jsx'
import standardSelectProps from '_lib/standardSelectProps'

import './styles.scss'

/**
 * Form for configuring delivery settings.
 *
 * Renders a form with validated inputs for:
 * - File format selection
 * - File compression selection
 * - Number of files input
 *
 * @param {Object} props
 * @param {Object} props.schema - Schema containing format and compression enums
 * @param {Object} props.data - Current form data values
 * @param {Function} props.onDataChange - Callback when form data changes
 * @param {boolean} props.requiredErrorFileFormat - Show required error for file format
 * @param {boolean} props.requiredErrorCompression - Show required error for compression
 * @param {boolean} props.requiredErrorNumFiles - Show required error for number of files
 */
const DeliveryFileConfigurationForm = ({
  schema = {},
  data = {},
  onDataChange,
  requiredErrorFileFormat,
  requiredErrorCompression,
  requiredErrorNumFiles,
}) => {
  const formatOptions = useMemo(
    () =>
      schema?.format?.enum?.map((option) => ({
        label: option,
        value: option,
      })),
    [schema]
  )

  const compressionOptions = useMemo(
    () =>
      schema?.compression?.enum?.map((option) => ({
        label: option,
        value: option,
      })),
    [schema]
  )

  return (
    <ValidatedForm
      inputs={[
        {
          label: 'File Format',
          required: requiredErrorFileFormat,
          validationClassName: 'delivery-file-configuration__required--file-configuration',
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-file-configuration__content-label',
                  'delivery-file-configuration__content-label--file-configuration'
                )}
              >
                File Format
              </span>
              <Select
                className={classNames('delivery-file-configuration__content-select', {
                  'delivery-file-configuration__required-field': requiredErrorFileFormat,
                })}
                value={data.format}
                onChange={({ value }) => onDataChange?.({ ...data, format: value })}
                options={formatOptions}
                {...standardSelectProps}
              />
            </>
          ),
        },
        {
          label: 'File Compression',
          required: requiredErrorCompression,
          validationClassName: 'delivery-file-configuration__required--file-configuration',
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-file-configuration__content-label',
                  'delivery-file-configuration__content-label--file-configuration'
                )}
              >
                File Compression
              </span>
              <Select
                className={classNames('delivery-file-configuration__content-select', {
                  'delivery-file-configuration__required-field': requiredErrorCompression,
                })}
                value={data.compression}
                onChange={({ value }) => onDataChange?.({ ...data, compression: value })}
                options={compressionOptions}
                {...standardSelectProps}
              />
            </>
          ),
        },
        {
          label: 'Number of files',
          validationClassName: 'delivery-file-configuration__required--file-configuration',
          required: requiredErrorNumFiles,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-file-configuration__content-label',
                  'delivery-file-configuration__content-label--file-configuration'
                )}
              >
                Number of files
              </span>
              <Input
                type="number"
                name="numFiles"
                min="1"
                max="1000"
                required
                className="delivery-file-configuration__content-select"
                value={data.numFiles}
                onChange={(value) => onDataChange?.({ ...data, numFiles: Number(value) })}
              />
            </>
          ),
        },
      ].filter(Boolean)}
    />
  )
}

DeliveryFileConfigurationForm.propTypes = {
  schema: PropTypes.shape({
    format: PropTypes.shape({
      enum: PropTypes.arrayOf(PropTypes.string),
    }),
    compression: PropTypes.shape({
      enum: PropTypes.arrayOf(PropTypes.string),
    }),
    numFiles: PropTypes.number,
  }),
  data: PropTypes.shape({
    format: PropTypes.string,
    compression: PropTypes.string,
    numFiles: PropTypes.number,
  }),
  onDataChange: PropTypes.func,
  requiredErrorFileFormat: PropTypes.bool,
  requiredErrorCompression: PropTypes.bool,
  requiredErrorNumFiles: PropTypes.bool,
}

export default DeliveryFileConfigurationForm
